import Vue from 'vue';
import types from '../mutation-types';
import ConversationApi from '../../api/inbox/conversation';
import {
  buildConversationContextKey,
  defaultConversationStats,
  hasConversationContextFilters,
} from 'dashboard/helper/conversationContext';

let activeConversationStatsRequest = null;

// Conversation counts are shared across assignee tabs for the same filter set.
// Ignoring assigneeType keeps the cached badge values stable while the list reloads.
export const buildConversationStatsContextKey = (params = {}) => {
  return buildConversationContextKey({
    ...params,
    assigneeType: undefined,
  });
};

const isRequestCanceled = error => {
  return error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';
};

const state = {
  activeContextKey: buildConversationStatsContextKey(),
  statsByContext: {},
};

export const getters = {
  getStats: $state => {
    return (
      $state.statsByContext[$state.activeContextKey] ||
      defaultConversationStats()
    );
  },
};

export const actions = {
  get: async ({ commit, state }, params) => {
    const contextKey = buildConversationStatsContextKey(params);
    let controller = null;

    if (activeConversationStatsRequest) {
      activeConversationStatsRequest.abort();
    }

    controller = new AbortController();
    activeConversationStatsRequest = controller;
    commit(types.SET_ACTIVE_CONV_STATS_CONTEXT, contextKey);
    try {
      const response = await ConversationApi.meta({
        ...params,
        signal: controller.signal,
      });
      const {
        data: { meta },
      } = response;
      if (state.activeContextKey !== contextKey) {
        return;
      }
      commit(types.SET_CONV_TAB_META, { contextKey, meta });
    } catch (error) {
      if (isRequestCanceled(error)) {
        return;
      }
      // Ignore error
    } finally {
      if (activeConversationStatsRequest === controller) {
        activeConversationStatsRequest = null;
      }
    }
  },
  set({ commit, state }, payload) {
    const meta = payload?.meta || payload;
    const params = payload?.params;
    const contextKey = hasConversationContextFilters(params)
      ? buildConversationStatsContextKey(params)
      : state.activeContextKey;

    commit(types.SET_CONV_TAB_META, { contextKey, meta });
  },
  setActiveContext({ commit }, params) {
    commit(
      types.SET_ACTIVE_CONV_STATS_CONTEXT,
      buildConversationStatsContextKey(params)
    );
  },
};

export const mutations = {
  [types.SET_ACTIVE_CONV_STATS_CONTEXT]($state, contextKey) {
    Vue.set($state, 'activeContextKey', contextKey);
  },
  [types.SET_CONV_TAB_META]($state, payload = {}) {
    const { contextKey = $state.activeContextKey, meta = payload } = payload;
    const {
      mine_count: mineCount = 0,
      unassigned_count: unAssignedCount = 0,
      all_count: allCount = 0,
      group_count: groupCount = 0,
    } = meta;

    Vue.set($state.statsByContext, contextKey, {
      mineCount,
      allCount,
      unAssignedCount,
      groupCount,
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
